import { useMemo } from 'react'
import { spring } from 'motion-dom'
import type { SpringConfig } from '../types'

export interface SpringCurvePoint {
  t: number
  value: number
}

export interface SpringCurveData {
  points: SpringCurvePoint[]
  durationMs: number
  overshootPercent: number
  maxValue: number
  minValue: number
}

export function useSpringCurveData(config: SpringConfig): SpringCurveData {
  return useMemo(() => {
    const generator = spring({
      keyframes: [0, 1],
      stiffness: config.stiffness,
      damping: config.damping,
      mass: config.mass,
    })

    // Step at 50ms intervals to find total duration (capped at 10s)
    const maxMs = 10000
    let durationMs = maxMs
    for (let t = 0; t <= maxMs; t += 50) {
      const { done } = generator.next(t)
      if (done) {
        durationMs = t
        break
      }
    }

    // Resample at 200 evenly-spaced points for a smooth SVG path
    const numPoints = 200
    const points: SpringCurvePoint[] = []
    let maxValue = 1
    let minValue = 0

    for (let i = 0; i <= numPoints; i++) {
      const t = (i / numPoints) * durationMs
      const { value } = generator.next(t)
      points.push({ t, value })
      if (value > maxValue) maxValue = value
      if (value < minValue) minValue = value
    }

    const overshootPercent = maxValue > 1 ? Math.round((maxValue - 1) * 100) : 0

    return { points, durationMs, overshootPercent, maxValue, minValue }
  }, [config.stiffness, config.damping, config.mass])
}
